import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import {
  Avatar,
  Box,
  Button,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalClose,
  ModalDialog,
  Option,
  Select,
  Stack,
} from '@mui/joy';
import { useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useUser } from '../../hooks/useUser';
import useBranches from '../../hooks/usebranches';
import {
  deleteUser as deleteUserAction,
  adminUpdateUser as adminUpdateUserAction,
} from '../../redux/actions/userActions';
import { EditUserModalActionType, ModalProps, User } from './types';

const UserModal = (props: ModalProps) => {
  const { setOpen, user, isOpen, refetch } = props;
  const { t } = useTranslation();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [loading, setLoading] = useState(false);

  const reducer = (state: User, action: EditUserModalActionType) => {
    const newState = { ...state, [action.field]: action.value };
    return newState;
  };
  const [state, dispatch] = useReducer(reducer, user);
  const reduxDispatch = useDispatch();
  const branchesQuery = useBranches();
  const userQuery = useUser();

  const deleteUser = async () => {
    setLoading(true);
    await reduxDispatch(deleteUserAction(state._id));
    setLoading(false);
    setOpen(false);
  };

  const updateUser = async () => {
    setLoading(true);
    await reduxDispatch(adminUpdateUserAction(state));
    setOpen(false);
    await refetch();
    setLoading(false);
    return true;
  };

  if (!branchesQuery.isSuccess || !userQuery.isSuccess) {
    return null;
  }

  const assignableRoles = ['USER'];
  if (userQuery.data.role === 'SUPER_ADMIN') {
    assignableRoles.push('ADMIN');
    assignableRoles.push('SUPER_ADMIN');
  } else if (userQuery.data.role === 'ADMIN') {
    assignableRoles.push('ADMIN');
  }

  const canEditRole =
    userQuery.data.role === 'SUPER_ADMIN' ||
    (userQuery.data.role === 'ADMIN' &&
      userQuery.data.branch.toLowerCase() === user.branch.toLowerCase());

  const canEditBranch = userQuery.data.role === 'SUPER_ADMIN';

  return (
    <Modal open={isOpen} onClose={() => setOpen(false)}>
      <ModalDialog size="md">
        <ModalClose />
        <DialogTitle>{t('admin.edit_user_title')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ marginTop: 2 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                marginBottom: 2,
              }}
            >
              <Avatar
                src={user.imageUrl}
                size="lg"
                sx={{ width: 64, height: 64 }}
              />
            </Box>
            <FormControl>
              <FormLabel>{t('admin.edit_user_firstName')}</FormLabel>
              <Input value={state.firstName} disabled variant="soft" />
            </FormControl>

            <FormControl>
              <FormLabel>{t('admin.edit_user_lastName')}</FormLabel>
              <Input
                value={state.lastName || 'Niet beschikbaar'}
                disabled
                variant="soft"
              />
            </FormControl>

            <FormControl>
              <FormLabel>{t('admin.edit_user_role')}</FormLabel>
              <Select
                value={state.role}
                onChange={(_, value) => {
                  if (value) {
                    dispatch({
                      field: 'role',
                      value: value,
                    });
                  }
                }}
                disabled={!canEditRole}
              >
                {assignableRoles.map((role) => (
                  <Option key={role} value={role}>
                    {role}
                  </Option>
                ))}
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel>{t('branch')}</FormLabel>
              <Select
                value={state.branch}
                onChange={(_, value) => {
                  if (value) {
                    dispatch({
                      field: 'branch',
                      value: value,
                    });
                  }
                }}
                disabled={!canEditBranch}
              >
                {branchesQuery.data.map((branch: any) => (
                  <Option key={branch.name} value={branch.name}>
                    {branch.name}
                  </Option>
                ))}
              </Select>
            </FormControl>

            <Divider />

            {!confirmDelete ? (
              <Stack direction="row" spacing={1}>
                <Button
                  fullWidth
                  color="primary"
                  onClick={updateUser}
                  loading={loading}
                >
                  Opslaan
                </Button>
                <Button
                  fullWidth
                  color="neutral"
                  variant="soft"
                  onClick={() => setOpen(false)}
                  disabled={loading}
                >
                  {t('cancel')}
                </Button>
                <Button
                  color="danger"
                  variant="soft"
                  startDecorator={<DeleteRoundedIcon />}
                  onClick={() => setConfirmDelete(true)}
                  disabled={loading}
                >
                  {t('delete')}
                </Button>
              </Stack>
            ) : (
              <Stack spacing={1}>
                <div
                  style={{
                    padding: '12px',
                    backgroundColor: '#FEE2E2',
                    borderRadius: '8px',
                    textAlign: 'center',
                  }}
                >
                  <p style={{ color: '#991B1B', fontWeight: 500 }}>
                    Weet je zeker dat je deze gebruiker wilt verwijderen?
                  </p>
                </div>
                <Stack direction="row" spacing={1}>
                  <Button
                    fullWidth
                    color="danger"
                    onClick={deleteUser}
                    loading={loading}
                  >
                    {t('yes')}, verwijderen
                  </Button>
                  <Button
                    fullWidth
                    color="neutral"
                    variant="soft"
                    onClick={() => setConfirmDelete(false)}
                    disabled={loading}
                  >
                    {t('no')}, annuleren
                  </Button>
                </Stack>
              </Stack>
            )}
          </Stack>
        </DialogContent>
      </ModalDialog>
    </Modal>
  );
};

export default UserModal;
